import { apiClient } from '@/lib/api/client'
import type { DashboardGetParams } from '@/lib/utils/dashboardQueryParams'

export interface DashboardStats {
  total_users: number
  total_media: number
  total_categories: number
  total_tags: number
}

export interface EnhancedDashboardStats extends DashboardStats {
  active_users: number
  new_users_today: number
  new_users_this_week: number
  new_users_this_month: number
  users_last_30_days: number
  total_pages: number
  published_pages: number
  draft_pages: number
  pages_this_month: number
  media_this_month: number
  activities_today: number
  activities_this_week: number
}

export interface DashboardWidget {
  id: number
  user_id: number | null
  widget_type: 'metric' | 'line_chart' | 'bar_chart' | 'pie_chart' | 'table'
  title: string
  data_source: string
  query_config: Record<string, any>
  visualization_config: Record<string, any>
  position_x: number
  position_y: number
  width: number
  height: number
  order_index: number
  active: boolean
  created_at: string
  updated_at: string
}

export interface ChartData {
  labels: string[]
  data: number[]
}

export interface DashboardKpis {
  shipping_line_count: number
  waybills_completed: number
  waybills_total: number
  waybills_remaining: number
  sales: number
  waybill_expenses: number
  parts_expense: number
  diesel_expense: number
  overdue_count: number
}

export interface DashboardOverduePaymentRow {
  shipping_line_name: string
  transaction_no: string
  overdue_payment_date: string | null
  overdue_payment_amount: number
  billing_statement_id: number | null
  statement_of_account_id: number
  due_date?: string | null
  soa_number?: string
}

export interface DashboardDieselByPlateRow {
  truck_plate_number: string
  amount: number
}

export interface DashboardReceivableEntityRow {
  shipping_line_name: string
  amount: number
}

export interface DashboardReceivablesByEntity {
  total: number
  entities: DashboardReceivableEntityRow[]
}

export interface DashboardBudgetCategoryTotals {
  issued_budget: number
  truck_trip_budget: number
  parts: number
  others: number
  driver_cash_advance: number
  helper_cash_advance: number
}

export interface DashboardDailyBudgetChartRow {
  date: string
  income: number
  expense: number
}

export interface DashboardSalesOverviewRow {
  month: string
  income: number
  waybill_expenses: number
}

export interface DashboardPayload {
  date_from: string
  date_to: string
  kpis: DashboardKpis
  sales_overview: DashboardSalesOverviewRow[]
  overdue_count: number
  overdue_payments: DashboardOverduePaymentRow[]
  diesel_by_plate: DashboardDieselByPlateRow[]
  receivables_by_entity: DashboardReceivablesByEntity
  budget_category_totals: DashboardBudgetCategoryTotals
  daily_budget_chart: DashboardDailyBudgetChartRow[]
}

export const dashboardService = {
  /**
   * Combined dashboard payload (KPIs, chart, overdue list) for the selected filter.
   */
  getDashboard: async (params: DashboardGetParams): Promise<DashboardPayload> => {
    const response = await apiClient.get('/dashboard', {
      params: params as Record<string, string | number>,
    })
    return response.data.data
  },

  /**
   * Get basic dashboard statistics
   */
  getStats: async (): Promise<DashboardStats> => {
    const response = await apiClient.get('/dashboard/stats')
    return response.data.data
  },

  /**
   * Get enhanced dashboard statistics
   */
  getEnhancedStats: async (): Promise<EnhancedDashboardStats> => {
    const response = await apiClient.get('/dashboard/stats')
    return response.data.data
  },

  /**
   * Get all dashboard widgets
   */
  getWidgets: async (): Promise<DashboardWidget[]> => {
    const response = await apiClient.get('/dashboard/widgets')
    return response.data.data
  },

  /**
   * Get widget data
   */
  getWidgetData: async (widgetId: number): Promise<any> => {
    const response = await apiClient.get(`/dashboard/widgets/${widgetId}/data`)
    return response.data.data
  },

  /**
   * Get user registration trend
   */
  getUserRegistrationTrend: async (
    period: 'daily' | 'weekly' | 'monthly' = 'daily',
    days: number = 30
  ): Promise<ChartData> => {
    const response = await apiClient.get('/dashboard/user-registration-trend', {
      params: { period, days },
    })
    return response.data.data
  },

  /**
   * Get pages by status
   */
  getPagesByStatus: async (): Promise<ChartData> => {
    const response = await apiClient.get('/dashboard/pages-by-status')
    return response.data.data
  },
}

